import { useState } from 'react'
import { AlertTriangle, Eye, KeyRound, MessageSquare, Save, ScanLine, ShieldAlert } from 'lucide-react'
import { ErrorState, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useDocumentTypes } from '@/features/document'
import { toApiProblem } from '@/lib/api/client'
import { useAiProposals, useAiSettings, useAiSettingsMutation } from '../api'
import {
  PROVIDER_DATA_NOTE, PROVIDER_LABELS, TOOL_GROUP_LABELS, TOOL_GROUP_NOTES,
} from '../types'
import type { AiSettingsView, AssistantToolGroup, ExtractionProvider } from '../types'

const PROVIDERS: ExtractionProvider[] = ['OCR_MRZ', 'ANTHROPIC', 'OPENAI_COMPATIBLE']

/**
 * The assistant cannot use the on-device OCR.
 *
 * <p>It reads the machine-readable zone of a document and returns nine fields. It is not a
 * model and cannot hold a conversation, so offering it here would be offering a choice that
 * the server refuses — which is exactly what it does, with that sentence.
 */
const ASSISTANT_PROVIDERS: ExtractionProvider[] = ['ANTHROPIC', 'OPENAI_COMPATIBLE']

const TOOL_GROUPS: AssistantToolGroup[] = ['SELF', 'LOOKUP', 'PROPOSAL']

/**
 * Turning document extraction on, for this company.
 *
 * <p>Off until somebody switches it on, and the screen is built around what that decision
 * actually commits to rather than around the fields. Two things are therefore stated on the
 * page rather than in documentation: which provider sends the document where, and that
 * redaction does not apply to the image.
 */
export function AiSettingsPage() {
  useBreadcrumb()
  const settings = useAiSettings()

  if (settings.isError) return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
  if (settings.isLoading || !settings.data) {
    return <Skeleton className="h-96 w-full" />
  }
  // Mounted once, when the settings first arrive, and it keeps its own state from then on.
  //
  // Deliberately not keyed on dataUpdatedAt: saving writes the response back into the query,
  // which changes that key, which remounts the form — discarding the "Settings saved"
  // confirmation in the same tick it was set, so a successful save looked like nothing had
  // happened. A settings form should also not have a background refetch overwrite what
  // somebody is in the middle of typing.
  return <AiSettingsForm current={settings.data} />
}

function AiSettingsForm({ current }: { current: AiSettingsView }) {
  const types = useDocumentTypes('EMPLOYEE')
  const proposals = useAiProposals()
  const save = useAiSettingsMutation()

  const [enabled, setEnabled] = useState(current.enabled)
  const [provider, setProvider] = useState<ExtractionProvider>(current.provider)
  const [baseUrl, setBaseUrl] = useState(current.baseUrl ?? '')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(current.model ?? '')
  const [allowed, setAllowed] = useState<string[]>(current.allowedDocumentTypes ?? [])
  const [redact, setRedact] = useState(current.redactTextBeforeCall)
  const [maxMb, setMaxMb] = useState(String(Math.round(current.maxFileBytes / (1024 * 1024))))
  const [minConfidence, setMinConfidence] = useState(String(current.minConfidence))
  const [assistantEnabled, setAssistantEnabled] = useState(current.assistantEnabled)
  const [assistantProvider, setAssistantProvider] =
    useState<ExtractionProvider>(
      current.assistantProvider === 'OCR_MRZ' ? 'ANTHROPIC' : current.assistantProvider)
  const [assistantModel, setAssistantModel] = useState(current.assistantModel ?? '')
  const [toolGroups, setToolGroups] = useState<string[]>(current.assistantToolGroups ?? [])
  const [maxOutputTokens, setMaxOutputTokens] = useState(String(current.assistantMaxOutputTokens))
  const [residencyNote, setResidencyNote] = useState(current.assistantDataResidencyNote ?? '')
  const [turnsPerHour, setTurnsPerHour] = useState(String(current.assistantTurnsPerHour))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const remote = provider !== 'OCR_MRZ'

  /**
   * Whether the endpoint and key are needed at all.
   *
   * <p>Both halves share one endpoint and one key — one row, one provider — so the fields
   * belong to whichever half currently needs them. Gating them on the extraction provider
   * alone left a company running the on-device OCR with no way to type the assistant's
   * endpoint: the assistant refused to switch on for want of a URL, and the box to put the
   * URL in was not on the page.
   */
  const needsRemoteCredentials = remote || assistantEnabled
  const ocrBlocked = provider === 'OCR_MRZ' && !current.ocrAvailable

  /** Everything that stops this configuration from being switched on. */
  const blockers: string[] = []
  if (enabled && ocrBlocked) {
    blockers.push(current.ocrUnavailableReason ?? 'The on-device OCR cannot run on this server.')
  }
  if (enabled && remote && !model.trim()) {
    blockers.push('Name the model to use.')
  }
  if (enabled && provider === 'OPENAI_COMPATIBLE' && !baseUrl.trim()) {
    blockers.push('Give the endpoint URL. There is no default, on purpose.')
  }
  // Anthropic only. A self-hosted OpenAI-compatible endpoint — vLLM, Ollama — routinely
  // takes no key, and that is the configuration where the document never leaves the
  // customer's network, so it is the one worth supporting rather than the one to block.
  if (enabled && provider === 'ANTHROPIC' && !apiKey.trim() && !current.apiKeySet) {
    blockers.push('Store an API key. Anthropic cannot be called without one.')
  }
  if (enabled && allowed.length === 0) {
    blockers.push('Choose at least one document type. Extraction runs only on types you enrol.')
  }
  if (assistantEnabled && !assistantModel.trim()) {
    blockers.push('Name the model the assistant should use.')
  }
  if (assistantEnabled && assistantProvider === 'OPENAI_COMPATIBLE' && !baseUrl.trim()) {
    blockers.push('The assistant needs the endpoint URL above. There is no default, on purpose.')
  }
  if (assistantEnabled && assistantProvider === 'ANTHROPIC' && !apiKey.trim() && !current.apiKeySet) {
    blockers.push('Store an API key. Anthropic cannot be called without one.')
  }
  if (assistantEnabled && toolGroups.length === 0) {
    blockers.push('Give the assistant at least one group of tools, or it can only make small talk.')
  }

  async function onSave() {
    setError(null)
    setSaved(false)
    try {
      await save.mutateAsync({
        enabled,
        provider,
        baseUrl: baseUrl.trim() || null,
        // Omitted when untouched: the stored key is never sent to this screen, so sending
        // back an empty string would clear it on every save.
        ...(apiKey ? { apiKey } : {}),
        model: model.trim() || null,
        allowedDocumentTypes: allowed,
        redactTextBeforeCall: redact,
        maxFileBytes: Math.max(1, Number(maxMb) || 10) * 1024 * 1024,
        minConfidence: Number(minConfidence) || 0.7,
        assistantEnabled,
        assistantProvider,
        assistantModel: assistantModel.trim() || null,
        assistantToolGroups: toolGroups,
        assistantMaxOutputTokens: Math.max(256, Number(maxOutputTokens) || 1024),
        assistantDataResidencyNote: residencyNote.trim() || null,
        assistantTurnsPerHour: Math.max(1, Number(turnsPerHour) || 60),
      })
      setApiKey('')
      setSaved(true)
    } catch (problem) {
      setError(toApiProblem(problem)?.detail ?? 'Those settings could not be saved')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI & assistant"
        subtitle="Two features, one provider and one key. Both are off until you switch them on, and neither saves anything without a person confirming it."
      />

      {!current.configured && (
        <Card className="border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          Extraction has not been set up for this company yet. It is off, and stays off until
          you turn it on here.
        </Card>
      )}

      <Card className="space-y-5 p-5">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <ScanLine className="h-4 w-4" aria-hidden /> Document extraction
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Read a scanned document and propose what it says. Nothing is saved without a person
            confirming it.
          </p>
        </div>

        <div className="flex items-start gap-3">
          <input
            id="ai-enabled"
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <div>
            <Label htmlFor="ai-enabled" className="text-base">Extraction is on</Label>
            <p className="text-sm text-slate-600">
              When this is off, the Extract button on a person&rsquo;s Documents tab is
              disabled and the API refuses with a 403.
            </p>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">Provider</legend>
          {PROVIDERS.map((option) => (
            <label
              key={option}
              className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 has-checked:border-[var(--color-primary)] has-checked:bg-[var(--color-primary-soft)]"
            >
              <input
                type="radio"
                name="ai-provider"
                className="mt-1 h-4 w-4"
                value={option}
                checked={provider === option}
                onChange={() => setProvider(option)}
              />
              <span>
                <span className="block text-sm font-medium">{PROVIDER_LABELS[option]}</span>
                <span className="block text-sm text-slate-600">{PROVIDER_DATA_NOTE[option]}</span>
                {option === 'OCR_MRZ' && !current.ocrAvailable && (
                  <span className="mt-1 flex items-start gap-1.5 text-sm text-amber-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    {current.ocrUnavailableReason ?? 'Not available on this server.'}
                  </span>
                )}
              </span>
            </label>
          ))}
        </fieldset>

        {needsRemoteCredentials && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ai-base-url">
                Endpoint {provider === 'ANTHROPIC' && <span className="text-slate-500">(optional)</span>}
              </Label>
              <Input
                id="ai-base-url"
                value={baseUrl}
                placeholder={provider === 'ANTHROPIC' ? 'https://api.anthropic.com' : 'https://vllm.internal:8000/v1'}
                onChange={(event) => setBaseUrl(event.target.value)}
              />
            </div>
            {remote && (
              <div>
                <Label htmlFor="ai-model">Model</Label>
                <Input
                  id="ai-model"
                  value={model}
                  placeholder={provider === 'ANTHROPIC' ? 'claude-sonnet-5' : 'qwen2.5-vl'}
                  onChange={(event) => setModel(event.target.value)}
                />
                <p className="mt-1 text-sm text-slate-600">
                  Reads documents. The assistant&rsquo;s model is set below.
                </p>
              </div>
            )}
            {!remote && (
              <p className="self-end text-sm text-slate-600">
                These serve the assistant. Extraction is on the on-device reader and sends
                nothing here.
              </p>
            )}
            <div className="sm:col-span-2">
              <Label htmlFor="ai-key">
                API key
                {provider === 'OPENAI_COMPATIBLE' && (
                  <span className="ms-2 text-sm font-normal text-slate-500">
                    optional — a self-hosted endpoint usually needs none
                  </span>
                )}
                {current.apiKeySet && (
                  <span className="ms-2 inline-flex items-center gap-1 text-sm font-normal text-emerald-700">
                    <KeyRound className="h-3.5 w-3.5" aria-hidden /> a key is stored
                  </span>
                )}
              </Label>
              <Input
                id="ai-key"
                type="password"
                autoComplete="off"
                value={apiKey}
                placeholder={current.apiKeySet ? 'Leave blank to keep the stored key' : 'sk-…'}
                onChange={(event) => setApiKey(event.target.value)}
              />
              <p className="mt-1 text-sm text-slate-600">
                Stored encrypted and never sent back to this screen — which is why it shows as
                blank rather than as dots standing in for a value.
                {provider === 'OPENAI_COMPATIBLE'
                  ? ' Leave it empty for a server on your own network that does not check one;'
                    + ' no Authorization header is sent in that case.'
                  : ''}
              </p>
            </div>
          </div>
        )}

        <div>
          <Label>Document types this may read</Label>
          <p className="mb-2 text-sm text-slate-600">
            Extraction runs only on the types you tick. Enrolling everything at once is what
            this list exists to prevent.
          </p>
          {types.isLoading && <Skeleton className="h-24 w-full" />}
          {types.data?.length === 0 && (
            <p className="text-sm text-slate-600">
              No employee document types exist yet. Seed or create one first.
            </p>
          )}
          <div className="grid gap-1 sm:grid-cols-2">
            {types.data?.map((type) => (
              <label key={type.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={allowed.includes(type.id)}
                  onChange={(event) =>
                    setAllowed((previous) =>
                      event.target.checked
                        ? [...previous, type.id]
                        : previous.filter((id) => id !== type.id))}
                />
                {type.name} <span className="text-slate-500">({type.code})</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ai-max-mb">Largest file to send (MB)</Label>
            <Input
              id="ai-max-mb"
              type="number"
              min={1}
              value={maxMb}
              onChange={(event) => setMaxMb(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ai-min-confidence">Preselect fields above this confidence</Label>
            <Input
              id="ai-min-confidence"
              type="number"
              step="0.05"
              min={0}
              max={1}
              value={minConfidence}
              onChange={(event) => setMinConfidence(event.target.value)}
            />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <input
            id="ai-redact"
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={redact}
            onChange={(event) => setRedact(event.target.checked)}
          />
          <div>
            <Label htmlFor="ai-redact">Redact text before sending</Label>
            <p className="text-sm text-slate-600">
              Applies to text the server sends alongside a document.
            </p>
          </div>
        </div>

        {/* The caveat that has to be on the screen, not in a manual. */}
        <div
          role="note"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            <strong>Redaction does not apply to the document image.</strong> The scan itself is
            sent to the provider as it is — a passport photograph, a name and a number, all of
            it. There is no way to redact a picture of a document and still read it, so with a
            remote provider selected, assume the document leaves your infrastructure intact.
            The on-device OCR option is the one that keeps it here.
          </p>
        </div>

      </Card>

      {/*
        The assistant, on the same screen and saved by the same button, because both halves
        live in one row and both are the same decision: which provider may see this company's
        data. Splitting them across two screens produced two answers to that question.
      */}
      <Card className="space-y-5 p-5">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <MessageSquare className="h-4 w-4" aria-hidden /> Assistant
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            A chat panel in the top bar. It answers with the signed-in person&rsquo;s own
            permissions &mdash; it can see what they can open on screen, and nothing else.
          </p>
        </div>

        <div className="flex items-start gap-3">
          <input
            id="assistant-enabled"
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={assistantEnabled}
            onChange={(event) => setAssistantEnabled(event.target.checked)}
          />
          <div>
            <Label htmlFor="assistant-enabled" className="text-base">The assistant is on</Label>
            <p className="text-sm text-slate-600">
              Off until you switch it on. While it is off the panel does not appear and the
              endpoint refuses with a 403.
            </p>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">Provider</legend>
          {ASSISTANT_PROVIDERS.map((option) => (
            <label
              key={option}
              className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 has-checked:border-[var(--color-primary)] has-checked:bg-[var(--color-primary-soft)]"
            >
              <input
                type="radio"
                name="assistant-provider"
                className="mt-1 h-4 w-4"
                value={option}
                checked={assistantProvider === option}
                onChange={() => setAssistantProvider(option)}
              />
              <span>
                <span className="block text-sm font-medium">{PROVIDER_LABELS[option]}</span>
                <span className="block text-sm text-slate-600">
                  {option === 'ANTHROPIC'
                    ? 'Questions, and the answers to them, are sent to Anthropic.'
                    : 'Questions are sent to the endpoint configured above. A model on your own'
                      + ' network is the configuration where nothing leaves the building.'}
                </span>
              </span>
            </label>
          ))}
          <p className="text-sm text-slate-600">
            The endpoint and API key above are shared with extraction. One provider, one key.
          </p>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="assistant-model">Model</Label>
            <Input
              id="assistant-model"
              value={assistantModel}
              placeholder={assistantProvider === 'ANTHROPIC' ? 'claude-sonnet-5' : 'qwen2.5-instruct'}
              onChange={(event) => setAssistantModel(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="assistant-max-tokens">Longest answer (tokens)</Label>
            <Input
              id="assistant-max-tokens"
              type="number"
              min={256}
              value={maxOutputTokens}
              onChange={(event) => setMaxOutputTokens(event.target.value)}
            />
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-medium">What it may do</legend>
          <p className="mb-2 text-sm text-slate-600">
            Each group is still gated on the permissions the person already holds. Ticking a
            group widens what the assistant may attempt, never what somebody may see.
          </p>
          <div className="space-y-2">
            {TOOL_GROUPS.map((group) => (
              <label
                key={group}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 has-checked:border-[var(--color-primary)] has-checked:bg-[var(--color-primary-soft)]"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={toolGroups.includes(group)}
                  onChange={(event) =>
                    setToolGroups((previous) =>
                      event.target.checked
                        ? [...previous, group]
                        : previous.filter((existing) => existing !== group))}
                />
                <span>
                  <span className="block text-sm font-medium">{TOOL_GROUP_LABELS[group]}</span>
                  <span className="block text-sm text-slate-600">{TOOL_GROUP_NOTES[group]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="assistant-turns">Questions per person per hour</Label>
            <Input
              id="assistant-turns"
              type="number"
              min={1}
              value={turnsPerHour}
              onChange={(event) => setTurnsPerHour(event.target.value)}
            />
            <p className="mt-1 text-sm text-slate-600">
              A cap on cost and on a runaway script, not on curiosity. The company as a whole
              is capped at twenty times this.
            </p>
          </div>
          <div>
            <Label htmlFor="assistant-residency">Where this data goes</Label>
            <Input
              id="assistant-residency"
              value={residencyNote}
              placeholder="e.g. Processed in the EU by our own vLLM instance"
              onChange={(event) => setResidencyNote(event.target.value)}
            />
            <p className="mt-1 text-sm text-slate-600">
              Shown to whoever asks where their questions are processed. The product cannot
              know this for a provider you configure, so it is yours to state.
            </p>
          </div>
        </div>

        {/* What redaction does and does not do, for the assistant specifically. */}
        <div
          role="note"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p>
              <strong>Redaction is pattern matching, not anonymisation.</strong> With
              &ldquo;Redact text before sending&rdquo; ticked above, the assistant replaces
              emails, phone numbers, Emirates ID and IBAN-shaped strings, and employee codes
              with placeholders before the question leaves this server, and puts them back in
              the answer.
            </p>
            <p className="mt-2">
              It does not redact a name written in prose, a job title, a salary figure, or
              anything the person&rsquo;s own question reveals by describing it. Treat the
              provider as seeing the substance of every question and answer, and choose the
              provider on that basis.
            </p>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <p className="font-medium">What the assistant is never given</p>
          <p className="mt-1">
            Payroll runs cannot be advanced, employment status cannot be changed, and workflow
            decisions on other people&rsquo;s requests cannot be made by asking. There is no
            tool for any of them at any setting &mdash; the assistant links to the screen and
            a person does it there.
          </p>
        </div>
      </Card>

      {/* One Save for both halves: they are one row, and one PUT writes it. */}
      <div className="space-y-3">
        {blockers.length > 0 && (
          <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Before this can be switched on:</p>
            <ul className="ms-5 list-disc">
              {blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
            </ul>
          </div>
        )}

        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {saved && <p role="status" className="text-sm text-emerald-700">Settings saved.</p>}

        <div className="flex justify-end">
          <Button onClick={() => void onSave()} disabled={save.isPending || blockers.length > 0}>
            <Save className="me-2 h-4 w-4" aria-hidden />
            Save settings
          </Button>
        </div>
      </div>

      <Card className="p-5">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold">
          <Eye className="h-4 w-4" aria-hidden /> Extraction log
        </h2>
        <p className="mb-3 text-sm text-slate-600">
          Every proposal, and what a person decided about it. A provider whose proposals are
          always edited is one that is not working; one whose proposals are never edited is
          worth checking that anybody is reading them.
        </p>
        {proposals.data?.length === 0 && (
          <p className="text-sm text-slate-600">Nothing has been extracted yet.</p>
        )}
        {proposals.data && proposals.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="text-start text-slate-600">
                <tr>
                  <th className="py-1.5 pe-4 font-medium">Provider</th>
                  <th className="py-1.5 pe-4 font-medium">Model</th>
                  <th className="py-1.5 pe-4 font-medium">Decision</th>
                  <th className="py-1.5 pe-4 font-medium">Took</th>
                </tr>
              </thead>
              <tbody>
                {proposals.data.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="py-1.5 pe-4">{row.provider}</td>
                    <td className="py-1.5 pe-4 text-slate-600">{row.modelVersion ?? '—'}</td>
                    <td className="py-1.5 pe-4">{row.decision}</td>
                    <td className="py-1.5 pe-4 text-slate-600">
                      {row.latencyMs == null ? '—' : `${row.latencyMs} ms`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
